/**
 * MarkupPreview Component
 * Feature: 049-critique-photo-markup
 *
 * Displays a thumbnail preview of markup annotations on a photo.
 * Clicking the thumbnail opens a modal dialog with full-size preview showing
 * the photo with markup shapes and rendered comment text with @-mentions.
 */

import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { AdaptivePhoto } from '@/components/photos/AdaptivePhoto';
import type { AdaptivePhotoProps } from '@/components/photos/AdaptivePhoto';
import { MarkupCanvas } from './MarkupCanvas';
import type { MarkupShape } from '@/types/markup.types';
import { createMarkdownWithShapeReferences, SHAPE_REFERENCE_STYLES } from '@/utils/markdownShapeReferences';

export interface MarkupPreviewProps {
  /** Photo being annotated */
  photo: AdaptivePhotoProps['photo'];
  /** Markup shapes to display */
  shapes: MarkupShape[];
  /** Comment text with @-mentions */
  commentText?: string;
  /** Loading state */
  loading?: boolean;
  /** Disable built-in modal (when used inside another click handler) */
  disableModal?: boolean;
}

/**
 * Displays markup preview as thumbnail with click-to-expand modal
 *
 * @example
 * <MarkupPreview
 *   photo={photo}
 *   shapes={shapes}
 *   commentText="Great composition! @1 shows the leading lines, @2 marks the focal point."
 * />
 */
export function MarkupPreview({
  photo,
  shapes,
  commentText = '',
  loading = false,
  disableModal = false,
}: MarkupPreviewProps): JSX.Element {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Calculate photo aspect ratio
  const photoAspectRatio = (() => {
    const variant = photo.variants.find((v) => v.width && v.height);
    if (variant && variant.width && variant.height) {
      return variant.width / variant.height;
    }
    return 1;
  })();

  // Handle loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center w-60 h-60 bg-surface-2 rounded animate-pulse">
        <span className="text-xs text-muted">Loading...</span>
      </div>
    );
  }

  return (
    <>
      {/* Thumbnail (240x240px) */}
      <div className="inline-block">
        {disableModal ? (
          // When modal is disabled, render a div to allow parent click handlers
          <div className="relative block w-60 h-60 rounded overflow-hidden">
            {/* Thumbnail with markup overlay */}
            <div className="relative w-full h-full">
              <AdaptivePhoto photo={photo} className="w-full h-full object-contain" preferStorageUrl={true} />

              {/* Markup canvas overlay */}
              <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                <MarkupCanvas
                  shapes={shapes}
                  selectedShapeId={null}
                  readOnly={true}
                  photoAspectRatio={photoAspectRatio}
                />
              </div>
            </div>

            {/* Shape count badge overlay */}
            <div className="absolute bottom-0 left-0 right-0 bg-primary/90 text-primary-foreground text-xs py-0.5 px-1 text-center font-medium group-hover:bg-primary transition-colors">
              {shapes.length} Shape{shapes.length !== 1 ? 's' : ''}
            </div>

            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
              <span className="opacity-0 group-hover:opacity-100 text-white text-xs font-medium bg-black/50 px-2 py-1 rounded">
                Click to view
              </span>
            </div>
          </div>
        ) : (
          // When modal is enabled, render a button with built-in click handler
          <button
            onClick={() => setIsModalOpen(true)}
            className="relative block w-60 h-60 rounded overflow-hidden border-2 border-border hover:border-primary transition-colors cursor-pointer group"
            aria-label="View markup preview"
          >
            {/* Thumbnail with markup overlay */}
            <div className="relative w-full h-full">
              <AdaptivePhoto photo={photo} className="w-full h-full object-contain" preferStorageUrl={true} />

              {/* Markup canvas overlay */}
              <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                <MarkupCanvas
                  shapes={shapes}
                  selectedShapeId={null}
                  readOnly={true}
                  photoAspectRatio={photoAspectRatio}
                />
              </div>
            </div>

            {/* Shape count badge overlay */}
            <div className="absolute bottom-0 left-0 right-0 bg-primary/90 text-primary-foreground text-xs py-0.5 px-1 text-center font-medium group-hover:bg-primary transition-colors">
              {shapes.length} Shape{shapes.length !== 1 ? 's' : ''}
            </div>

            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
              <span className="opacity-0 group-hover:opacity-100 text-white text-xs font-medium bg-black/50 px-2 py-1 rounded">
                Click to view
              </span>
            </div>
          </button>
        )}
      </div>

      {/* Modal dialog with full-size preview */}
      {!disableModal && (
        <Dialog.Root open={isModalOpen} onOpenChange={setIsModalOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/80 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <Dialog.Content className="fixed left-[50%] top-[50%] z-50 h-[90vh] w-[90vw] translate-x-[-50%] translate-y-[-50%] flex flex-col rounded-lg border border-border bg-background p-6 shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">
            {/* Header */}
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
              <Dialog.Title className="text-lg font-semibold text-foreground">
                Markup Preview
              </Dialog.Title>
              <Dialog.Close className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span className="sr-only">Close</span>
              </Dialog.Close>
            </div>

            {/* Description */}
            <Dialog.Description className="text-sm text-muted mb-4 flex-shrink-0">
              Photo with {shapes.length} markup annotation{shapes.length !== 1 ? 's' : ''}.
              {commentText && ' See comment below for shape references.'}
            </Dialog.Description>

            {/* Content area - split horizontally */}
            <div className="flex-1 flex flex-col lg:flex-row gap-4 overflow-hidden">
              {/* Photo with markup (left side or top on mobile) */}
              <div className="flex-1 flex flex-col overflow-hidden">
                <h3 className="text-sm font-medium text-foreground mb-2">Annotated Photo</h3>
                <div className="flex-1 relative bg-surface-2 rounded overflow-hidden">
                  <AdaptivePhoto photo={photo} className="w-full h-full object-contain" preferStorageUrl={true} />

                  {/* Markup canvas overlay */}
                  <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                    <MarkupCanvas
                      shapes={shapes}
                      selectedShapeId={null}
                      readOnly={true}
                      photoAspectRatio={photoAspectRatio}
                    />
                  </div>
                </div>
              </div>

              {/* Comment text (right side or bottom on mobile) */}
              {commentText && (
                <div className="flex-1 flex flex-col overflow-hidden">
                  <h3 className="text-sm font-medium text-foreground mb-2">Critique Comment</h3>
                  <div className="flex-1 p-4 bg-surface-1 rounded border border-border overflow-auto">
                    <style>{SHAPE_REFERENCE_STYLES}</style>
                    <div
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{
                        __html: createMarkdownWithShapeReferences().render(commentText),
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Shape details */}
            <div className="mt-4 p-4 bg-surface-1 rounded border border-border flex-shrink-0">
              <h4 className="text-sm font-medium text-foreground mb-2">
                Markup Details ({shapes.length} shape{shapes.length !== 1 ? 's' : ''})
              </h4>
              <div className="flex flex-wrap gap-2">
                {shapes.map((shape) => (
                  <div
                    key={shape.id}
                    className="inline-flex items-center gap-2 px-2 py-1 bg-surface-2 rounded text-xs"
                  >
                    <span className="font-bold text-primary">#{shape.number}</span>
                    <span className="text-muted capitalize">{shape.type}</span>
                    <div
                      className="w-4 h-4 border rounded"
                      style={{
                        borderColor: shape.strokeColor,
                        borderWidth: 2,
                        borderStyle: shape.strokeDasharray ? 'dashed' : 'solid',
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      )}
    </>
  );
}

export default MarkupPreview;
